import axios, {
	type AxiosInstance,
	type AxiosRequestConfig,
	type AxiosResponse,
	type AxiosError,
} from 'axios';
import { logger } from '@mono-farmart-web/logger';

export type APIRequestConfig = AxiosRequestConfig;

export interface ApiClientConfig extends APIRequestConfig {
	baseURL: string;
}

class ApiService {
	private apiClient: AxiosInstance;

	constructor(config: ApiClientConfig) {
		this.apiClient = axios.create({
			...config,
			headers: {
				'Content-Type': 'application/json',
				...config.headers,
			},
		});
		logger.attachAxiosInterceptor(this.apiClient);

		this.apiClient.interceptors.response.use(
			(response: AxiosResponse) => response,
			(error: AxiosError<any>) => {
				if (!axios.isCancel(error)) {
					console.error('Error in API call', error);
				}
				return Promise.reject(error);
			},
		);
	}

	request = async <T>(config: APIRequestConfig): Promise<T> => {
		const response = await this.apiClient.request<T>(config);
		return response.data;
	};

	get = <T>(
		url: string,
		config?: Omit<APIRequestConfig, 'url' | 'method'>,
	): Promise<T> => {
		return this.request<T>({ ...config, url, method: 'GET' });
	};

	post = <T>(
		url: string,
		data?: any,
		config?: Omit<APIRequestConfig, 'url' | 'method' | 'data'>,
	): Promise<T> => {
		return this.request<T>({ ...config, url, method: 'POST', data });
	};

	put = <T>(
		url: string,
		data?: any,
		config?: Omit<APIRequestConfig, 'url' | 'method' | 'data'>,
	): Promise<T> => {
		return this.request<T>({ ...config, url, method: 'PUT', data });
	};

	delete = <T>(
		url: string,
		config?: Omit<APIRequestConfig, 'url' | 'method'>,
	): Promise<T> => {
		return this.request<T>({ ...config, url, method: 'DELETE' });
	};

	getClient = (): AxiosInstance => this.apiClient;
}

export const createApiClient = (config: ApiClientConfig): ApiService => {
	return new ApiService(config);
};

export type { ApiService };
